import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
  User,
  UserStats,
  CreateUserPayload,
  UpdateUserPayload,
  UsersResponse,
  UserResponse,
  UserStatsResponse,
} from '../models/user.model';

export interface UserProfile {
  id?: string;
  email: string;
  username: string;
  name: string;
  role: string;
  roleId?: string;               // MongoDB ObjectId reference to Role document
  status: string;
  tenantId?: string;            // Optional - root users may not have one
  twoFactorEnabled?: boolean;
  emailOtpEnabled?: boolean;    // When true, login requires email OTP as second factor
  isRoot?: boolean;             // true = superadmin with access to all tenants
  createdAt?: string;
  updatedAt?: string;
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  status?: string;
  role?: string;
  tenantId?: string;
}

export interface ResendFirstLoginResponse {
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly apiBaseUrl = (environment.apiBaseUrl || 'http://localhost:3000').replace(/\/+$/, '');
  private readonly apiUrl = `${this.apiBaseUrl}/api/users`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) { }

  // ================== PROFILE METHODS (existing) ==================

  getProfile(): Observable<UserProfile> {
    const url = `${this.apiUrl}/me`;
    return this.http.get<any>(url, { headers: this.buildHeaders() }).pipe(
      map((data) => this.mapProfile(data))
    );
  }

  updateProfile(payload: Partial<UserProfile>): Observable<UserProfile> {
    const url = `${this.apiUrl}/me`;
    return this.http.put<any>(url, payload, { headers: this.buildHeaders() }).pipe(
      map((data) => this.mapProfile(data))
    );
  }

  // ================== CRUD METHODS ==================

  /**
   * Get all users with pagination, sorting, and filtering
   */
  getUsers(page: number = 1, limit: number = 10, sort?: string, filters?: { [key: string]: any }): Observable<UsersResponse> {
    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('limit', Math.min(limit, 100).toString());

    if (sort) {
      httpParams = httpParams.set('sort', sort);
    }

    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (value !== null && value !== undefined && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }

    return this.http.get<UsersResponse>(this.apiUrl, {
      headers: this.buildHeaders(),
      params: httpParams,
    });
  }

  /**
   * Get a single user by ID
   */
  getUserById(id: string): Observable<User> {
    return this.http.get<UserResponse>(`${this.apiUrl}/${id}`, {
      headers: this.buildHeaders(),
    }).pipe(
      map((response) => response.data)
    );
  }

  /**
   * Create a new user
   */
  createUser(payload: CreateUserPayload): Observable<User> {
    return this.http.post<UserResponse>(this.apiUrl, payload, {
      headers: this.buildHeaders(),
    }).pipe(
      map((response) => response.data)
    );
  }

  /**
   * Update an existing user
   */
  updateUser(id: string, payload: UpdateUserPayload): Observable<User> {
    return this.http.patch<UserResponse>(`${this.apiUrl}/${id}`, payload, {
      headers: this.buildHeaders(),
    }).pipe(
      map((response) => response.data)
    );
  }

  /**
   * Toggle user status (activate/disable/lock)
   */
  toggleUserStatus(id: string, status: 'active' | 'locked' | 'disabled'): Observable<User> {
    return this.http.patch<UserResponse>(`${this.apiUrl}/${id}/status`, { status }, {
      headers: this.buildHeaders(),
    }).pipe(
      map((response) => response.data)
    );
  }

  /**
   * Delete a user
   */
  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.buildHeaders(),
    });
  }

  /**
   * Update user password (admin endpoint - no current password required)
   */
  updateUserPassword(id: string, password: string): Observable<User> {
    return this.http.patch<UserResponse>(`${this.apiUrl}/${id}/password`, { password }, {
      headers: this.buildHeaders(),
    }).pipe(
      map((response) => response.data)
    );
  }

  /**
   * Reset user MFA configuration (admin endpoint)
   */
  resetMFA(id: string): Observable<User> {
    return this.http.post<UserResponse>(`${this.apiUrl}/${id}/reset-mfa`, {}, {
      headers: this.buildHeaders(),
    }).pipe(
      map((response) => response.data)
    );
  }

  /**
   * Regenerate a temporary password and resend first-time login link to user.
   * Expected backend endpoint: POST /api/users/:id/resend-first-login
   */
  resendFirstLoginLink(id: string): Observable<ResendFirstLoginResponse> {
    return this.http.post<ResendFirstLoginResponse>(`${this.apiUrl}/${id}/resend-first-login`, {}, {
      headers: this.buildHeaders(),
    });
  }

  // ================== STATS METHODS ==================

  /**
   * Get user statistics (calculated dynamically on each request)
   */
  getUserStats(): Observable<UserStats> {
    return this.http.get<UserStatsResponse>(`${this.apiUrl}/stats`, {
      headers: this.buildHeaders(),
    }).pipe(
      map((response) => response.data)
    );
  }

  // ================== UNIQUE VALUES ==================

  /**
   * Get unique values for a specific field (for filter dropdowns)
   */
  getUniqueValues(field: string, tenantId?: string): Observable<string[]> {
    let httpParams = new HttpParams().set('field', field);

    if (tenantId) {
      httpParams = httpParams.set('tenantId', tenantId);
    }

    return this.http.get<{ values: string[] }>(`${this.apiUrl}/unique-values`, {
      headers: this.buildHeaders(),
      params: httpParams,
    }).pipe(
      map((response) => response.values)
    );
  }

  // ================== ANALYST METHODS ==================

  /**
   * Get users filtered by isRoot status, tenantId, and roleId
   * Used for getting analysts that can be assigned to cases
   * - isRoot=true: Returns analysts (root/admin users)
   * - isRoot=false: Returns external analysts (non-root users)
   * @param isRoot Whether to get root users (analysts) or non-root users (external analysts)
   * @param tenantId Optional tenant ID to filter by (users in the same tenant)
   * @param roleId Optional role ID to filter by (users with the same role)
   */
  getUsersByIsRoot(isRoot: boolean, tenantId?: string | null, roleId?: string | null): Observable<UserProfile[]> {
    const params: { [key: string]: string } = {
      isRoot: String(isRoot),
      // Cache-bust so we don't get 304 with stale list (e.g. old list including internal analysts)
      _t: Date.now().toString()
    };

    // Add tenantId filter if provided
    if (tenantId) {
      params['tenantId'] = tenantId;
    }

    // Add roleId filter if provided
    if (roleId) {
      params['roleId'] = roleId;
    }

    return this.http.get<any>(this.apiUrl, { headers: this.buildHeaders(), params }).pipe(
      map((response) => {
        // Handle both old format (array) and new format (with data property)
        const data = Array.isArray(response) ? response : (response.data || []);
        const users = data.map((item: any) => this.mapProfile(item));

        // Client-side filtering as safeguard - isRoot is the priority filter
        return users.filter((user: UserProfile) => {
          // Priority 1: Filter by isRoot status (required)
          // When current user is external (isRoot=false): only include users with explicit isRoot===false.
          // This prevents internal analysts from appearing when the backend omits isRoot on user objects.
          const userIsRoot = user.isRoot === true;
          const userIsExplicitlyNonRoot = user.isRoot === false;

          if (isRoot) {
            // Looking for internal analysts: exclude non-root and unknown
            if (!userIsRoot) return false;
          } else {
            // Looking for external analysts: include only explicitly non-root (exclude root and unknown)
            if (userIsRoot || !userIsExplicitlyNonRoot) return false;
          }

          // Priority 2: Filter by tenantId if provided
          if (tenantId && user.tenantId && user.tenantId !== tenantId) {
            return false;
          }

          // Priority 3: Filter by roleId if provided
          if (roleId && user.roleId && user.roleId !== roleId) {
            return false;
          }

          return true;
        });
      })
    );
  }

  /**
   * Get analysts (users with isRoot=true)
   * @param tenantId Optional tenant ID to filter by
   * @param roleId Optional role ID to filter by
   */
  getAnalysts(tenantId?: string | null, roleId?: string | null): Observable<UserProfile[]> {
    return this.getUsersByIsRoot(true, tenantId, roleId);
  }

  /**
   * Get external analysts (users with isRoot=false)
   * @param tenantId Optional tenant ID to filter by
   * @param roleId Optional role ID to filter by
   */
  getExternalAnalysts(tenantId?: string | null, roleId?: string | null): Observable<UserProfile[]> {
    return this.getUsersByIsRoot(false, tenantId, roleId);
  }

  /**
   * Page size when fetching all users (for counts).
   */
  private readonly allUsersLimit = 10;

  /**
   * Get all users without any filtering.
   */
  getAllUsers(): Observable<UserProfile[]> {
    return this.getUsers(1, this.allUsersLimit).pipe(
      switchMap((first) => {
        const pagination = first.pagination as { total?: number; totalPages?: number } | undefined;
        const total = pagination?.total ?? 0;
        const totalPages = pagination?.totalPages ?? 1;
        const firstData = (first.data || []).map((item: any) => this.mapProfile(item));

        // Single page or no pagination info: return first page only (may already be "all")
        if (!pagination || totalPages <= 1 || total <= firstData.length) {
          return of(firstData);
        }

        const remainingPages = Array.from(
          { length: totalPages - 1 },
          (_, i) => i + 2
        );
        const remaining$ = remainingPages.map((page) =>
          this.getUsers(page, this.allUsersLimit).pipe(
            map((res) => (res.data || []).map((item: any) => this.mapProfile(item)))
          )
        );

        return forkJoin(remaining$).pipe(
          map((pagesData) => firstData.concat(...pagesData))
        );
      })
    );
  }

  // ================== PRIVATE METHODS ==================

  private buildHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      Authorization: token || '',
      'Content-Type': 'application/json',
    });
  }

  private mapProfile(data: any): UserProfile {
    // Preserve explicit isRoot; support both camelCase (isRoot) and snake_case (is_root) from backend
    const rawIsRoot = data?.isRoot ?? data?.is_root;
    const isRoot = rawIsRoot === true ? true : rawIsRoot === false ? false : undefined;
    return {
      id: data?._id || data?.id,
      email: data?.email || '',
      username: data?.username || '',
      name: data?.name || '',
      role: data?.role || '',
      roleId: this.normalizeId(data?.roleId || data?.role?._id || data?.role?.id) || undefined,
      status: data?.status || '',
      tenantId: this.normalizeId(data?.tenantId || data?.tenant || data?.tenant_id) || undefined,
      twoFactorEnabled: data?.twoFactorEnabled,
      emailOtpEnabled: data?.emailOtpEnabled ?? data?.email_otp_enabled,
      isRoot,
      createdAt: data?.createdAt,
      updatedAt: data?.updatedAt,
    };
  }

  /**
   * Normalize MongoDB ObjectId to string for consistent comparison.
   */
  private normalizeId(value: any): string | undefined {
    if (value == null) return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    if (typeof value === 'object' && value?.$oid) return String(value.$oid).trim() || undefined;
    const s = String(value).trim();
    return s && s !== '[object Object]' ? s : undefined;
  }
}
